/**
 * Formatting for the step history page.
 *
 * Kept apart from the components because these are the only parts of the page a
 * test can reach — vitest runs in a node environment here, with no DOM — so the
 * fiddly arithmetic lives where it can be pinned down.
 */

import type { StepRunSummary } from "../api.js";

export interface RunGroup {
  jobId: string;
  jobType: string;
  /** The newest run in the group — what the list is ordered on, so it is also
   *  what the group's heading has to show if the page is to read newest-first. */
  latestAt: string;
  /** In the order they ran, oldest first — which is the order they read in. */
  runs: StepRunSummary[];
}

/**
 * The rail's rows, gathered into the jobs that produced them.
 *
 * One ingest records eight steps; listed flat they read as eight unrelated
 * events rather than one thing that happened. `runs` arrives newest-first, so
 * first appearance puts the jobs in that order too, and prepending within a
 * group turns each job's own steps back the right way up.
 *
 * A retried job records the same step twice, hours apart; both belong to the
 * one job and so to the one group, which is why the group is placed and dated
 * by its newest run rather than by when the job first started.
 */
export const groupRunsByJob = (runs: StepRunSummary[]): RunGroup[] => {
  const groups: RunGroup[] = [];
  const byJob = new Map<string, RunGroup>();

  for (const run of runs) {
    let group = byJob.get(run.jobId);
    if (!group) {
      group = { jobId: run.jobId, jobType: run.jobType, latestAt: run.startedAt, runs: [] };
      byJob.set(run.jobId, group);
      groups.push(group);
    }
    group.runs.unshift(run);
  }

  return groups;
};

/** How many runs of a step keep their snapshot. Mirrors the worker's
 *  `SNAPSHOT_RETENTION`, quoted in copy so a missing diff reads as housekeeping
 *  rather than as data loss. */
export const SNAPSHOTS_KEPT = 10;

/**
 * A step duration.
 *
 * Sub-second steps are common — `uploaded` is a metadata write — and rounding
 * them to "0.0s" would claim the pipeline does nothing between the slow parts.
 */
export const formatDuration = (ms: number | null): string => {
  if (ms === null) return "—";
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)}s`;
  const total = Math.round(ms / 1000);
  return `${Math.floor(total / 60)}m ${total % 60}s`;
};

export const formatBytes = (bytes: number): string => {
  const units = ["B", "KB", "MB", "GB", "TB"];
  let value = Math.abs(bytes);
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${bytes < 0 ? "-" : ""}${unit === 0 ? value : value.toFixed(1)} ${units[unit]}`;
};

/** Summary and stat keys are camelCase because they are database columns and
 *  JSON fields. The page is prose, so they are unpicked here rather than shown raw. */
export const formatKey = (key: string): string => {
  const spaced = key
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .trim();
  if (spaced === "") return key;
  return spaced.charAt(0).toUpperCase() + spaced.slice(1).toLowerCase();
};

/**
 * One value out of a step summary or a diff's stats.
 *
 * The shapes are whatever the worker wrote at the time, so every branch has to
 * survive a value it did not expect — a summary that predates a field is null,
 * and `byType` is an object where its neighbours are numbers.
 */
export const formatValue = (key: string, value: unknown): string => {
  if (value === null || value === undefined) return "—";
  if (typeof value === "boolean") return value ? "yes" : "no";
  if (typeof value === "number") {
    if (!Number.isFinite(value)) return "—";
    if (/bytes$/i.test(key)) return formatBytes(value);
    return Number.isInteger(value) ? value.toLocaleString() : value.toFixed(2);
  }
  if (typeof value === "string") return value === "" ? "—" : value;
  return JSON.stringify(value);
};

/**
 * When a run started, at the coarseness the reader needs.
 *
 * A bare clock time is ambiguous the moment a project spans two days, which it
 * does as soon as anyone comes back to a recording tomorrow.
 */
export const formatWhen = (iso: string, now: Date = new Date()): string => {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return "—";
  return at.toDateString() === now.toDateString()
    ? at.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" })
    : at.toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
};
